import { computed, Ref, ref } from '@vue/composition-api';
import { createInjection } from '@/lib/createInjection';

type RequestGuardContext = {
  isRequesting: Ref<boolean>;
  doRequest: <T>(fn: () => Promise<T>, errorMessage?: string) => Promise<T>;
};

type ErrorHandler = (errorMessage: string) => void;

const { provide, inject } = createInjection<RequestGuardContext>('requestGuardContext');

const provideRequestGuard = (): RequestGuardContext => {
  const isRequestingRef = ref(false);
  const requestPromises = ref<Promise<unknown>[]>([]);

  const isRequesting = computed<boolean>(() => isRequestingRef.value);

  const queueRequest = (promise: Promise<unknown>): void => {
    isRequestingRef.value = true;
    requestPromises.value.push(promise);

    promise
      .catch((err) => {
        console.error('[doRequest error caught internally]', err);
      })
      .finally(() => {
        requestPromises.value = requestPromises.value.filter(p => p !== promise);
        isRequestingRef.value = requestPromises.value.length > 0;
      });
  };

  const doRequest = async<T> (fn: () => Promise<T>, errorMessage?: string): Promise<T> => {
    const promise = fn();
    queueRequest(promise);
    try {
      return await promise;
    } catch (err) {
      console.error('[doRequest error]', errorMessage);
      throw err;
    }
  };

  return {
    isRequesting,
    doRequest,
  };
};

export const provideRequestGuardContext = (): void => {
  const requestGuard = provideRequestGuard();
  provide(requestGuard);
};

export const useRequestGuard = (errorHandler?: ErrorHandler): RequestGuardContext => {
  const context = inject();

  if (!errorHandler) {
    return context;
  }

  // Override doRequest to include error handling
  const doRequest = async<T> (fn: () => Promise<T>, errorMessage?: string): Promise<T> => {
    try {
      return await context.doRequest(fn, errorMessage);
    } catch (err: any) {
      errorHandler(errorMessage || err?.message || 'API request error');
      throw err;
    }
  };

  return {
    isRequesting: context.isRequesting,
    doRequest,
  };
};
